/**
 * The email-safe blocks the marketing builder offers, and the user's saved
 * blocks loaded from `GET /marketing/blocks`.
 *
 * ⚠️ EVERY BLOCK IS A TABLE, AND EVERY STYLE IS INLINE ON THE ELEMENT. Email
 * clients ignore most of CSS — Outlook renders with Word's engine, Gmail strips
 * `<style>` in places — so the safe vocabulary is nested `role="presentation"`
 * tables with attributes AND inline styles saying the same thing twice. The
 * preset's inliner (`gjs-get-inlined-html`) inlines whatever the style manager
 * adds on top.
 *
 * ⚠️ THE COLOURS HERE ARE EMAIL CONTENT, NOT APP CHROME. They are literal hex
 * on purpose: they are what a recipient's mail client renders, which has no
 * access to this app's tokens and no dark-theme switch of ours. The house
 * "tokens only" rule is about the EMERGE interface, and the builder's chrome
 * follows it (see `builder-theme.ts`).
 *
 * Every section is the same two-table shape: a full-width outer table carrying
 * the page background, holding a centred inner table capped at 600px — the
 * width almost every email client renders comfortably. Columns carry the class
 * `emerge-col`, which the exported document's `<style>` stacks below 620px.
 */
import type { Editor } from 'grapesjs';

/** The class the exported document's media query stacks on small screens. */
export const COLUMN_CLASS = 'emerge-col';

const PAGE_BG = '#f4f4f5';
const CARD_BG = '#ffffff';
const INK = '#1f2937';
const MUTED = '#6b7280';
const ACCENT = '#1a1f4e';
const FONT = 'Arial, Helvetica, sans-serif';

/**
 * A neutral grey placeholder an author replaces through the asset manager.
 * ⚠️ A data URI on purpose: an external placeholder service is a third-party
 * request from every draft, and a broken one looks like a broken builder.
 */
function placeholderImage(width: number, height: number, label: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    `<rect width="100%" height="100%" fill="#e5e7eb"/>` +
    `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="${Math.max(12, Math.round(height / 6))}" fill="#6b7280">${label}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Outer page-coloured table → inner 600px card → one padded cell. */
function section(inner: string, padding = '16px 24px'): string {
  return (
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:${PAGE_BG};">` +
    `<tr><td align="center" style="padding:0 12px;">` +
    `<table role="presentation" class="emerge-container" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;margin:0 auto;background-color:${CARD_BG};">` +
    `<tr><td style="padding:${padding};font-family:${FONT};color:${INK};">${inner}</td></tr>` +
    `</table></td></tr></table>`
  );
}

const heading = (text: string, size = 24) =>
  `<h1 style="margin:0 0 12px 0;font-family:${FONT};font-size:${size}px;line-height:1.3;font-weight:bold;color:${INK};">${text}</h1>`;

const paragraph = (text: string) =>
  `<p style="margin:0 0 12px 0;font-family:${FONT};font-size:15px;line-height:1.6;color:${INK};">${text}</p>`;

const LOREM =
  'Write your message here. Keep it short and specific, and tell the reader what you would like them to do next.';

function columns(count: 2 | 3): string {
  const width = Math.floor(100 / count);
  const cell = (n: number) =>
    `<td class="${COLUMN_CLASS}" width="${width}%" valign="top" style="width:${width}%;padding:8px;vertical-align:top;font-family:${FONT};color:${INK};">` +
    `<h2 style="margin:0 0 8px 0;font-family:${FONT};font-size:18px;line-height:1.3;color:${INK};">Column ${n}</h2>` +
    paragraph('Short supporting text for this column.') +
    `</td>`;
  const cells = Array.from({ length: count }, (_, i) => cell(i + 1)).join('');
  return section(
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;"><tr>${cells}</tr></table>`,
    '16px',
  );
}

/**
 * The bulletproof button: a table cell carrying the background colour, so the
 * button stays a button in clients that ignore padding on links (Outlook).
 */
export function buttonHtml(label = 'Call to action', href = 'https://'): string {
  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:8px auto;">` +
    `<tr><td align="center" bgcolor="${ACCENT}" style="border-radius:6px;background-color:${ACCENT};">` +
    `<a href="${href}" target="_blank" style="display:inline-block;padding:12px 28px;font-family:${FONT};font-size:15px;font-weight:bold;line-height:1.2;color:#ffffff;text-decoration:none;border-radius:6px;">${label}</a>` +
    `</td></tr></table>`
  );
}

const SOCIAL = ['LinkedIn', 'Instagram', 'Facebook', 'YouTube']
  .map(
    (name) =>
      `<a href="https://" target="_blank" style="font-family:${FONT};font-size:13px;color:${ACCENT};text-decoration:underline;margin:0 8px;">${name}</a>`,
  )
  .join('');

interface EmailBlock {
  id: string;
  label: string;
  category: string;
  content: string;
}

const LAYOUT = 'Layout';
const CONTENT = 'Content';

/** The house blocks, in the order the panel lists them. */
export const EMAIL_BLOCKS: readonly EmailBlock[] = [
  { id: 'emerge-section-1', label: '1 column', category: LAYOUT, content: section(heading('Section heading') + paragraph(LOREM)) },
  { id: 'emerge-section-2', label: '2 columns', category: LAYOUT, content: columns(2) },
  { id: 'emerge-section-3', label: '3 columns', category: LAYOUT, content: columns(3) },
  {
    id: 'emerge-logo-header',
    label: 'Logo header',
    category: LAYOUT,
    content: section(
      `<img src="${placeholderImage(160, 48, 'Logo')}" alt="Logo" width="160" style="display:block;margin:0 auto;width:160px;max-width:100%;height:auto;border:0;">`,
      '20px 24px',
    ),
  },
  {
    id: 'emerge-footer-text',
    label: 'Footer text',
    category: LAYOUT,
    content: section(
      `<p style="margin:0;font-family:${FONT};font-size:12px;line-height:1.6;color:${MUTED};text-align:center;">You are receiving this email because of your relationship with SEED Global Education.</p>`,
      '16px 24px',
    ),
  },
  { id: 'emerge-heading', label: 'Heading', category: CONTENT, content: section(heading('Your heading here')) },
  { id: 'emerge-text', label: 'Text', category: CONTENT, content: section(paragraph(LOREM)) },
  {
    id: 'emerge-image',
    label: 'Image',
    category: CONTENT,
    content: section(
      `<img src="${placeholderImage(552, 276, 'Image')}" alt="" width="552" style="display:block;width:100%;max-width:552px;height:auto;border:0;">`,
    ),
  },
  { id: 'emerge-button', label: 'Button', category: CONTENT, content: section(buttonHtml()) },
  {
    id: 'emerge-divider',
    label: 'Divider',
    category: CONTENT,
    content: section(
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;"><tr><td style="border-top:1px solid #e5e7eb;font-size:0;line-height:0;height:1px;">&nbsp;</td></tr></table>`,
      '8px 24px',
    ),
  },
  {
    id: 'emerge-spacer',
    label: 'Spacer',
    category: CONTENT,
    content: section(`<div style="height:24px;line-height:24px;font-size:24px;">&nbsp;</div>`, '0 24px'),
  },
  {
    id: 'emerge-social',
    label: 'Social links',
    category: CONTENT,
    content: section(`<p style="margin:0;text-align:center;">${SOCIAL}</p>`, '12px 24px'),
  },
  {
    id: 'emerge-video',
    label: 'Video link',
    category: CONTENT,
    content: section(
      `<a href="https://" target="_blank" style="display:block;text-decoration:none;">` +
        `<img src="${placeholderImage(552, 310, 'Play video')}" alt="Watch the video" width="552" style="display:block;width:100%;max-width:552px;height:auto;border:0;">` +
        `</a>` +
        `<p style="margin:8px 0 0 0;font-family:${FONT};font-size:13px;color:${MUTED};text-align:center;">Watch the video</p>`,
    ),
  },
];

/** What a starter template opens on, so a new template is never a blank white rectangle. */
export const STARTER_HTML: string =
  section(heading('Your headline') + paragraph(LOREM) + buttonHtml(), '24px');

export function registerEmailBlocks(editor: Editor): void {
  const bm = editor.BlockManager;
  for (const block of EMAIL_BLOCKS) {
    bm.add(block.id, {
      label: block.label,
      category: block.category,
      content: block.content,
      select: true,
    });
  }
}

/** The shape of a saved block this module needs — a subset of `MarketingBlock`. */
export interface SavedBlockLike {
  id: number;
  kind: 'header' | 'footer' | 'section';
  name: string;
  html: string;
}

const SAVED_CATEGORY: Record<SavedBlockLike['kind'], string> = {
  header: 'Saved headers',
  footer: 'Saved footers',
  section: 'Saved sections',
};

const SAVED_PREFIX = 'emerge-saved-';

/**
 * A saved block's html is whatever its own builder exported. If that was a
 * whole document, only what sits inside `<body>` (plus any `<style>`) may be
 * dropped into another email — a second `<html>` inside a template is not
 * something any mail client agrees about.
 */
export function bodyFragment(html: string): string {
  if (!/<(html|body|head)[\s>]/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const styles = Array.from(doc.querySelectorAll('style'))
    .map((s) => `<style>${s.textContent ?? ''}</style>`)
    .join('');
  return styles + doc.body.innerHTML;
}

/**
 * Replaces the saved-block set wholesale, so a re-fetch never doubles a block
 * up and a block archived since the last fetch disappears from the panel.
 */
export function registerSavedBlocks(editor: Editor, blocks: readonly SavedBlockLike[]): void {
  const bm = editor.BlockManager;
  for (const existing of [...bm.getAll().models]) {
    if (String(existing.getId()).startsWith(SAVED_PREFIX)) bm.remove(existing.getId());
  }
  for (const block of blocks) {
    bm.add(`${SAVED_PREFIX}${block.id}`, {
      label: block.name,
      category: SAVED_CATEGORY[block.kind] ?? 'Saved sections',
      content: bodyFragment(block.html),
      select: true,
    });
  }
}
